import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const lineReveal = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.15 },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (d) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: d },
  }),
};

export const Hero = ({ ready = true }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  const scrollToChapters = () =>
    document.getElementById("chapters")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-screen min-h-[620px] overflow-hidden bg-ink">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src="/assets/hero.jpg"
          alt="Industrial manufacturing facility"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/45" />

      <div className="relative z-10 mx-auto flex h-full w-full max-w-[1400px] flex-col justify-end px-6 pb-16 sm:px-10 lg:px-16 lg:pb-24">
        <motion.p
          custom={0.15}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          data-testid="hero-eyebrow"
          className="mb-6 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.35em] text-white/70"
        >
          <span className="inline-block h-2 w-2 bg-brand" />
          Electromechanical Switch Manufacturer
        </motion.p>

        <h1 className="font-heading text-[12.5vw] font-black leading-[0.95] tracking-tighter text-white sm:text-7xl lg:text-8xl xl:text-[7.5rem]">
          <span className="block overflow-hidden pb-1">
            <motion.span custom={0} variants={lineReveal} initial="hidden" animate="visible" className="block">
              The switches behind
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-3">
            <motion.span custom={1} variants={lineReveal} initial="hidden" animate="visible" className="block">
              India&rsquo;s <span className="text-brand">leading brands.</span>
            </motion.span>
          </span>
        </h1>

        <motion.p
          custom={0.85}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          data-testid="hero-subline"
          className="mt-6 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg"
        >
          Devkii Electricals manufactures electromechanical switches and precision
          switching components for OEMs and businesses across India.
        </motion.p>

        <motion.div custom={1} variants={fadeUp} initial="hidden" animate="visible" className="mt-10">
          <button
            data-testid="hero-cta"
            onClick={scrollToChapters}
            className="group inline-flex items-center gap-3 bg-brand px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-white hover:text-ink"
          >
            Explore our capabilities
            <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-1" />
          </button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-16 right-10 z-10 hidden flex-col items-center gap-4 lg:flex"
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] font-medium uppercase tracking-[0.35em] text-white/50 [writing-mode:vertical-rl]">
          Scroll
        </span>
        <motion.span
          animate={{ scaleY: [1, 0.4, 1], opacity: [1, 0.4, 1] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          className="block h-16 w-px origin-top bg-white/50"
        />
      </motion.div>
    </section>
  );
};
